// =====================================================================
// commande-prete/sscc.js — Commande prête Nespart (13-10-2026)
// =====================================================================
// SSCC — jumeau de backend/src/lib/sscc.js : le PDA prévient AVANT l'appel
// serveur, le serveur reste le garde-fou. Toute règle se change dans les deux.
//
// Deux formes d'étiquette acceptées (décision Silviu 14/10) :
//  - SSCC GS1 = 18 chiffres, le dernier = clé de contrôle GS1 (mod 10, poids
//    3,1 depuis la droite). Un lecteur GS1-128 peut renvoyer « (00)… », « 00… »
//    (20 chiffres) ou un préfixe « ]C1 », et le séparateur GS (\u001d) : retirés ;
//  - étiquette INTERNE EffiTrace des étiquettes du dépôt : palette
//    « SKC.HG.PAL.1970 », colis « SKC.HG.026897 » — lettre d'abord, 3 à 6
//    segments séparés par des points, 18 caractères au plus (export .097 M-009).
// Le scan est normalisé (trim, MAJUSCULES) : les doublons se comparent sur cette forme.
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

export const SSCC_MAX = 18;
export const MSG_SSCC_FORMATS = 'SSCC : 18 chiffres GS1 ou étiquette EffiTrace du type SKC.HG.PAL.1970';
export const MSG_ETIQ_PAL_SUR_COLIS = 'Étiquette de palette scannée sur un colis : vérifiez';
export const MSG_ETIQ_COLIS_SUR_PAL = 'Étiquette de colis scannée sur une palette : vérifiez';
const EFFITRACE = /^[A-Z][A-Z0-9]*(\.[A-Z0-9]+){2,5}$/;

export const cleGs1 = (d17) => {
  let s = 0;
  for (let i = 0; i < 17; i++) s += Number(d17[16 - i]) * (i % 2 === 0 ? 3 : 1);
  return String((10 - (s % 10)) % 10);
};

// Préfixe AIM du lecteur retiré : « ]C1 » (GS1-128) et « ]C0 » (Code 128 simple, étiquettes EffiTrace).
// Espaces et parenthèses retirés d'un GS1 seulement : dans une étiquette EffiTrace, une espace est une
// faute de frappe (« SKC.HG.PAL 1970 ») qui doit faire refuser la saisie, pas donner une autre étiquette.
export function normSscc(raw) {
  const s = String(raw ?? '').trim().toUpperCase().replace(/^\]C\d/, '').replace(/\u001d/g, '');
  const compact = s.replace(/[\s()]/g, '');
  if (!/^\d+$/.test(compact)) return s;
  return /^\d{20}$/.test(compact) && compact.startsWith('00') ? compact.slice(2) : compact;
}

// SSCC raccourci pour l'affichage : un GS1 (18 chiffres) se lit par sa fin « …123456 » ; une
// étiquette EffiTrace (18 caractères au plus) s'affiche ENTIÈRE — deux étiquettes peuvent finir
// pareil (SKC.HG.PAL.1970 / SKC.XX.PAL.1970).
export const abregeSscc = (s) => (/^\d{18}$/.test(String(s)) ? `…${String(s).slice(-6)}` : String(s));

export const ssccGs1Valide = (s) => /^\d{18}$/.test(s) && cleGs1(s.slice(0, 17)) === s[17];
export const etiquetteEffitrace = (s) => typeof s === 'string' && s.length <= SSCC_MAX && EFFITRACE.test(s);
export const ssccValide = (s) => ssccGs1Valide(s) || etiquetteEffitrace(s);

// Message de la fiche pour un SSCC (normalisé, non vide) refusé ; null s'il est bon.
// « chiffres lus / clé fausse » (mots de la maquette) : seulement pour une saisie ENTIÈREMENT numérique.
export function motifSsccInvalide(s) {
  if (ssccValide(s)) return null;
  if (/^\d+$/.test(s)) {
    if (s.length !== 18) return `SSCC : ${s.length} chiffre(s) lu(s), 18 attendus`;
    return 'SSCC : chiffre de contrôle faux — rescannez l\'étiquette';
  }
  if (EFFITRACE.test(s)) return `${MSG_SSCC_FORMATS} (${SSCC_MAX} caractères au plus, ${s.length} lus)`;
  return MSG_SSCC_FORMATS;
}

// Étiquette d'un autre type que la fiche (NON bloquant) : segment « PAL » sur un colis, ou
// étiquette EffiTrace SANS « PAL » sur une palette. Une étiquette GS1 ne dit rien du type.
export function avertEtiquette(sscc, type) {
  const s = normSscc(sscc);
  if (!etiquetteEffitrace(s)) return null;
  const pal = s.split('.').includes('PAL');
  if (type === 'COL' && pal) return MSG_ETIQ_PAL_SUR_COLIS;
  if (type === 'PAL' && !pal) return MSG_ETIQ_COLIS_SUR_PAL;
  return null;
}
